import { z } from 'zod'

import { GetTimeSchema } from './time'

// Mirrors platform-api `LoadDuplicateTemplateBuilder::build()` (`GET /loads/:loadId/duplicate`):
// the Original Load's details in the shape of the `POST /loads/:loadId/duplicate` body.

const PaymentTermsTemplateSchema = z.object({
  days: z.number().nullish(),
  period: z.string().nullish(),
  trigger: z.string(),
})

export const GetLoadDuplicateStopSchema = z.object({
  cargo_operations: z.string().nullish(),
  // Always null in the template; the Duplicate sets it.
  date: z.string().nullish(),
  location_id: z.number().nullish(),
  // The location's name when the Stop was saved.
  name: z.string().nullish(),
  order: z.number(),
  // Always null in the template; the Duplicate sets it.
  reference_no: z.string().nullish(),
  time: GetTimeSchema.nullish(),
  type: z.enum(['collection', 'delivery']),
})

export const GetLoadDuplicateTemplateSchema = z.object({
  assignee_ids: z.number().array(),
  carrier_payment_terms: PaymentTermsTemplateSchema.nullish(),
  carrier_rate: z
    .object({
      amount: z.number(),
      currency: z.string(),
      notes: z.string().nullish(),
      payment_method: z.string().nullish(),
    })
    .nullish(),
  company_id: z.number(),
  contact_id: z.number().nullish(),
  // PHP sends an empty map as a JSON list.
  custom_attributes: z.union([z.record(z.string(), z.json()), z.json().array()]),
  customer_rate: z
    .object({ amount: z.number(), currency: z.string(), description: z.string().nullish() })
    .nullish(),
  customs: z
    .object({
      notes: z.string().nullish(),
      required: z.boolean(),
      responsible_party: z.string().nullish(),
    })
    .nullish(),
  drops: GetLoadDuplicateStopSchema.array(),
  end_carrier_company_id: z.number().nullish(),
  end_carrier_contact_id: z.number().nullish(),
  end_customer_company_id: z.number().nullish(),
  end_customer_contact_id: z.number().nullish(),
  estimated_costs: z
    .object({
      amount: z.number(),
      category: z.string(),
      currency: z.string(),
      notes: z.string().nullish(),
      payment_method: z.string().nullish(),
    })
    .array(),
  goods: z.number().array(),
  goods_description: z.string().nullish(),
  haulier_company_id: z.number().nullish(),
  haulier_contact_id: z.number().nullish(),
  haulier_notes: z.string().nullish(),
  height: z.number().nullish(),
  length: z.number().nullish(),
  load_type_id: z.number().nullish(),
  notes: z.string().nullish(),
  organisation_id: z.number().nullish(),
  packaging_type_id: z.number().nullish(),
  pallets: z.union([z.number(), z.string()]).nullish(),
  payment_terms: PaymentTermsTemplateSchema.nullish(),
  pod_requirement_description: z.string().nullish(),
  pod_requirement_id: z.number().nullish(),
  requirements: z.object({ adr: z.boolean(), dvs: z.boolean(), waste_license: z.boolean() }),
  source_load_id: z.number(),
  tags: z.string().array(),
  temperature: z.union([z.number(), z.string()]).nullish(),
  urgent: z.boolean(),
  // The Original Load's owner; a Load may have none.
  user_id: z.number().nullish(),
  weight: z.number().nullish(),
  weight_unit: z.string().nullish(),
  width: z.number().nullish(),
})

export type GetLoadDuplicateTemplate = z.output<typeof GetLoadDuplicateTemplateSchema>
